import React from 'react';
import Config from '../../constants/appConfig';
import FilterDramaIcon from '@material-ui/icons/FilterDrama';
import Skeleton from '@material-ui/lab/Skeleton';
import { withStyles, Tooltip, MenuItem, Select, FormControl, Button, InputLabel } from '@material-ui/core';
import Moment from 'moment';
import thermalInversionExample from '../../assets/images/thermalInversionExample.png';
import normalFlowExample from '../../assets/images/normalFlowExample.png';
import DeltaTHeat from '../../components/heat_graph/deltaTGraph';
import windArrowImage from '../../assets/images/marker/waypoint/marker-icon.png';
import Chart from 'chart.js/auto';
import L from 'leaflet';
import 'leaflet-draw';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import SpeedIcon from '@material-ui/icons/Speed';
import img from '../../routes/point/img';
import Tools from '../util/tools';
import mobileWeatherStation from '../../assets/images/estacao-movel.png';
import fixedWeatherStation from '../../assets/images/estacao-fixa.png';

const styles = theme => ({
    select: {
        color: 'white',
        '&:before': {
            borderColor: 'transparent',
        },
        '&:after': {
            borderColor: 'transparent',
        },
        '& .MuiSelect-icon': {
            color: 'white',
        },
    },
    windDirectionContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        position: 'relative',
        width: '100%',
    },
    circle: {
        position: 'relative',
        width: '3rem',
        height: '3rem',
        borderRadius: '50%',
        border: '1px solid white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '0.5rem',
    },
    windDirection: {
        position: 'absolute',
        fontSize: '0.6rem',
        color: 'white',
    },
    north: {
        top: '-2px',
        left: '50%',
        transform: 'translateX(-50%)',
    },
    south: {
        bottom: '-2px',
        left: '50%',
        transform: 'translateX(-50%)',
    },
    east: {
        right: '2px',
        top: '50%',
        transform: 'translateY(-50%)',
    },
    west: {
        left: '2px',
        top: '50%',
        transform: 'translateY(-50%)',
    },
    windDirectionArrow: {
        position: 'absolute',
        top: '48%',
        left: '52%',
        transform: 'translate(-50%, -50%) rotate(180deg)',
        transition: 'transform 0.3s ease-in-out',
        width: '1rem',
        height: '1.25rem',
    },
});

class DiaryClimate extends React.Component {
    state = {
        stationData: '',
        temperature: '',
        air_temperature: '',
        air_humidity: '',
        humidity: '',
        windSpeed: '',
        windSpeedRaw: null,
        deltaT: '',
        windDirection: '',
        thermalInversion: '',
        pressure: '',
        datetime: '',
        loading: true,
        stations: [],
        selectedStation: '',
        date: Moment().format('YYYY-MM-DD'),
        intervalId: null,
        flightConditions: '',
        rainData: [],
        windSpeedUnit: this.props.res.LINGUA === 'en-US' ? this.props.res.MPH : this.props.res.KMH,
        refreshInterval: 300000
    };
    existingIcons = {};
    coverageCircle = null;
    coverageArrows = [];
    coverageAnimationFrame = null;
    coverageArrowTracks = [];
    refreshInFlight = false;

    isEnglishLanguage = () => {
        return this.props.res && this.props.res.LINGUA === 'en-US';
    };

    getWindSpeedUnitLabel = (unit) => {
        if (!unit) return '';
        return String(unit).toUpperCase();
    };

    getWindSpeedStorageKey = () => {
        return this.isEnglishLanguage() ? 'windSpeedEn' : 'windSpeedDefault';
    };

    getInitialWindSpeedUnit = () => {
        const { res } = this.props;
        const savedUnit = localStorage.getItem(this.getWindSpeedStorageKey());

        if (this.isEnglishLanguage()) {
            if (savedUnit === res.MPH || savedUnit === res.KMH) {
                return savedUnit;
            }
            return res.MPH;
        }

        if (savedUnit === res.KMH || savedUnit === res.MS) {
            return savedUnit;
        }

        return res.KMH;
    };

    getNextWindSpeedUnit = (currentUnit) => {
        const { res } = this.props;

        if (this.isEnglishLanguage()) {
            return currentUnit === res.MPH ? res.KMH : res.MPH;
        }

        return currentUnit === res.KMH ? res.MS : res.KMH;
    };

    isValidWeatherValue = (value) => {
        return value !== undefined && value !== null && value !== -99 && !isNaN(Number(value));
    };

    formatTemperature = (value) => {
        const { res } = this.props;

        if (!this.isValidWeatherValue(value)) {
            return '-';
        }

        if (this.isEnglishLanguage()) {
            return `${Tools.c_to_f(Number(value)).toFixed(1)} ${res.F}`;
        }

        return `${Number(value).toFixed(1)} ${res.C}`;
    };

    formatWindSpeed = (value, unit) => {
        const { res } = this.props;

        if (!this.isValidWeatherValue(value)) {
            return '-';
        }

        const speed = Number(value);
        const selectedUnit = unit || this.state.windSpeedUnit;

        if (selectedUnit === res.KMH) {
            return `${Tools.mps_to_kmh(speed).toFixed(1)} ${res.KMH}`;
        }

        if (selectedUnit === res.MPH) {
            return `${Tools.mps_to_mph(speed).toFixed(1)} ${res.MPH}`;
        }

        if (selectedUnit === res.FTS) {
            return `${Tools.mps_to_fts(speed).toFixed(1)} ${res.FTS}`;
        }

        return `${speed.toFixed(1)} ${res.MS}`;
    };

    formatDisplayDateTime = (datetime) => {
        if (!datetime) {
            return '-';
        }

        if (this.isEnglishLanguage()) {
            return Moment(datetime).format('MM/DD/YYYY HH:mm');
        }

        return Moment(datetime).format(this.props.res.FORMAT_HORAMINDATA);
    };

    componentDidMount() {
        this.icon = img;

        const savedInterval = parseInt(localStorage.getItem('refreshInterval'), 10) || this.state.refreshInterval;
        const savedWindSpeedUnit = this.getInitialWindSpeedUnit();

        this.setState({ refreshInterval: savedInterval, windSpeedUnit: savedWindSpeedUnit }, () => {
            if (!this.mymap) {
                this.mymap = L.map('mapid', { zoomControl: false, attributionControl: false }).setView(Config.place, 14);
                L.tileLayer(Config.tile_google, Config.tile_google_config).addTo(this.mymap);
                this.featureGroupP = L.featureGroup().addTo(this.mymap);
                this.mymap.on('zoomend', this.refreshStationMarkerIcons);
            }

            this.listStations().then(() => {
                if (this.state.stations.length > 0) {
                    this.setState({ selectedStation: this.state.stations[0].id }, () => this.updateAllStationsData());
                }
            });

            this.startRefreshInterval();
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.refreshInterval !== this.state.refreshInterval) {
            clearInterval(this.state.intervalId);
            this.startRefreshInterval();
        }

        if (this.mymap && prevState.stations !== this.state.stations) {
            setTimeout(() => {
                this.mymap.invalidateSize();
            }, 0);
        }
    }

    componentWillUnmount() {
        clearInterval(this.state.intervalId);
        this.removeCoverageVisuals();
        if (this.mymap) {
            this.mymap.off('zoomend', this.refreshStationMarkerIcons);
            this.mymap.off();
            this.mymap.remove();
            this.mymap = null;
        }
        if (this.rainChart) this.rainChart.destroy();
    }

    handleIntervalChange = (event) => {
        const newInterval = parseInt(event.target.value, 10) * 60000;

        localStorage.setItem('refreshInterval', newInterval);

        this.setState({ refreshInterval: newInterval }, () => {
            clearInterval(this.state.intervalId);
            this.startRefreshInterval();
        });
    }

    startRefreshInterval() {
        if (this.state.intervalId) {
            clearInterval(this.state.intervalId);
        }

        const intervalId = setInterval(() => {
            this.updateAllStationsData();
        }, this.state.refreshInterval);

        this.setState({ intervalId });
    }

    updateAllStationsData = async () => {
        if (this.refreshInFlight || !this.state.stations.length) return;
        this.refreshInFlight = true;
        try {
            const stations = [...this.state.stations];
            let nextIndex = 0;
            const worker = async () => {
                while (nextIndex < stations.length) {
                    const station = stations[nextIndex];
                    nextIndex += 1;
                    await this.getDataById(station.id);
                }
            };

            // Evita abrir uma requisição por estação simultaneamente e não
            // repete a consulta da fonte que já está selecionada.
            await Promise.all([worker(), worker(), worker()]);
        } catch (error) {
            console.error(error);
        } finally {
            this.refreshInFlight = false;
        }
    };

    getStationMarkerSize = () => {
        const zoom = this.mymap ? this.mymap.getZoom() : 14;
        return Math.max(26, Math.min(50, Math.round(28 + (zoom - 10) * 2.75)));
    };

    getStationMarkerIcon = (station) => {
        const source = this.state.stations.find(item => String(item.id) === String(station.id_source));
        const model = String(source && source.model || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const isMobile = model.includes('movel');
        const size = this.getStationMarkerSize();
        const ageInMinutes = station.datetime ? Moment().diff(Moment(station.datetime), 'minutes') : null;
        // Mantém a mesma tolerância de horário da tela de Clima. Assim, uma
        // pequena diferença de fuso/relógio entre API e navegador não faz uma
        // estação recém-atualizada parecer offline.
        const status = ageInMinutes !== null && ageInMinutes <= 20
            ? 'online'
            : station.datetime ? 'offline' : 'empty';
        const statusColor = status === 'online' ? '#24a148' : status === 'offline' ? '#d92d20' : '#98a2b3';

        // O card preserva sua estética, mas o preenchimento do marcador indica
        // imediatamente se a estação está online, offline ou sem dados.
        return L.divIcon({
            className: 'diary-climate-station-marker-wrap',
            html: `<span style="display:grid;width:${size}px;height:${size}px;place-items:center;overflow:hidden;background:${statusColor};border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.42)"><img src="${isMobile ? mobileWeatherStation : fixedWeatherStation}" alt="" style="width:${isMobile ? '82%' : '64%'};height:${isMobile ? '82%' : '90%'};object-fit:contain" /></span>`,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
            popupAnchor: [0, -size / 2]
        });
    };

    refreshStationMarkerIcons = () => {
        Object.entries(this.existingIcons).forEach(([id, marker]) => {
            const station = marker && marker.__stationData;
            if (station) marker.setIcon(this.getStationMarkerIcon(station));
        });
    };

    drawMarker = (stationIcon) => {
        const dIcon = this.getStationMarkerIcon(stationIcon);

        let newLatLng = new L.LatLng(stationIcon.lat, stationIcon.lng);
        let existingIcon = this.existingIcons[stationIcon.id_source];
        if (existingIcon) {
            existingIcon.setLatLng(newLatLng);
            existingIcon.setIcon(dIcon);
            existingIcon.__stationData = stationIcon;
            return;
        }

        let myLayer = L.marker(newLatLng, { icon: dIcon })
            .on('click', () => {
                this.setState({
                    selectedStation: stationIcon.id_source,
                    loading: true
                }, () => {
                    this.getDataById(stationIcon.id_source);
                });
            });

        myLayer.__stationData = stationIcon;
        myLayer.addTo(this.featureGroupP);
        this.existingIcons[stationIcon.id_source] = myLayer;
    };

    removeCoverageVisuals = () => {
        if (this.coverageAnimationFrame) cancelAnimationFrame(this.coverageAnimationFrame);
        this.coverageAnimationFrame = null;
        this.coverageArrowTracks = [];
        if (this.coverageCircle && this.mymap) this.mymap.removeLayer(this.coverageCircle);
        this.coverageCircle = null;
        this.coverageArrows.forEach(marker => {
            if (this.mymap) this.mymap.removeLayer(marker);
        });
        this.coverageArrows = [];
    };

    getDeltaTColor = (value) => {
        const delta = Number(value);
        if (!Number.isFinite(delta)) return '#98a2b3';
        if (delta >= 2 && delta <= 8) return '#2ca85e';
        if (delta > 10) return '#dc3545';
        return '#e6a700';
    };

    // A animação usa poucos marcadores para não pesar no card, mesmo em
    // estações lentas. A cor segue a mesma regra de Delta-T da tela de clima.
    updateCoverageVisuals = (station) => {
        if (!this.mymap) return;
        this.removeCoverageVisuals();

        const lat = Number(station.lat);
        const lng = Number(station.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const color = this.getDeltaTColor(station.delta_t);
        this.coverageCircle = L.circle([lat, lng], {
            radius: 5000,
            color,
            weight: 2,
            opacity: .9,
            fillColor: color,
            fillOpacity: .1,
            interactive: false
        }).addTo(this.mymap);

        const direction = Number(station.wind_direction);
        if (!Number.isFinite(direction)) return;

        const bearing = (direction + 180) % 360;
        const radians = bearing * Math.PI / 180;
        const metersPerLatitude = 111320;
        const metersPerLongitude = Math.max(1, metersPerLatitude * Math.cos(lat * Math.PI / 180));
        const lateralTracks = [-.55, 0, .55];
        const arrowsPerTrack = 4;

        lateralTracks.forEach((cross, track) => {
            const lateral = cross * 5000;
            const halfChord = Math.sqrt(Math.max(0, 5000 * 5000 - lateral * lateral)) * .9;
            for (let index = 0; index < arrowsPerTrack; index += 1) {
                const icon = L.divIcon({
                    className: 'diary-climate-wind-arrow-wrap',
                    html: `<span style="display:block;color:${color};font-size:1.1rem;line-height:1;text-shadow:0 1px 2px rgba(0,0,0,.45);transform:rotate(${bearing - 90}deg)">&#10148;</span>`,
                    iconSize: [20, 20],
                    iconAnchor: [10, 10]
                });
                const marker = L.marker([lat, lng], { icon, interactive: false, keyboard: false }).addTo(this.mymap);
                this.coverageArrows.push(marker);
                this.coverageArrowTracks.push({ marker, track, index, lateral, halfChord, lat, lng, radians, metersPerLatitude, metersPerLongitude });
            }
        });

        const windKmh = Math.max(0, Number(station.wind_speed) || 0) * 3.6;
        const duration = Math.max(8000, Math.min(30000, 60000 / Math.sqrt(Math.max(.1, windKmh))));
        const animate = now => {
            const basePhase = (now % duration) / duration;
            this.coverageArrowTracks.forEach(item => {
                const phase = (basePhase + item.index / arrowsPerTrack + (item.track % 2 ? .5 / arrowsPerTrack : 0)) % 1;
                const along = -item.halfChord + phase * item.halfChord * 2;
                const north = Math.cos(item.radians) * along - Math.sin(item.radians) * item.lateral;
                const east = Math.sin(item.radians) * along + Math.cos(item.radians) * item.lateral;
                item.marker.setLatLng([item.lat + north / item.metersPerLatitude, item.lng + east / item.metersPerLongitude]);
            });
            if (this.coverageArrowTracks.length) this.coverageAnimationFrame = requestAnimationFrame(animate);
        };
        this.coverageAnimationFrame = requestAnimationFrame(animate);
    };

    zoomInL = () => this.mymap && this.mymap.zoomIn();
    zoomOutL = () => this.mymap && this.mymap.zoomOut();

    listStations = () => {
        const apiUrl = `${Config.server}/weather_source`;
        const options = {
            method: 'GET',
            headers: {
                'Authorization': localStorage.getItem('access-token-jwt'),
            },
        };

        return fetch(apiUrl, options)
            .then((res) => {
                if (res.status !== 200) throw res;
                return res.json();
            })
            .then(
                (result) => {
                    const currentDate = new Date().toISOString().split('T')[0];
                    const updatedStations = result
                        .filter(station => station.datetime && station.datetime.startsWith(currentDate))
                        .sort((a, b) => new Date(b.datetime) - new Date(a.datetime));

                    this.setState({
                        stations: updatedStations,
                    });
                },
                (error) => {
                    console.error(error);
                }
            );
    };

    getDataById = (sourceID) => {
        const dayStart = new Date(`${this.state.date}T00:00:00`).getTime() / 1000;
        const dayEnd = new Date(`${this.state.date}T23:59:59`).getTime() / 1000;

        const apiUrl = `${Config.server}/weather_data/daily/${this.state.refreshInterval}/${sourceID}/${Math.floor(dayStart)}/${Math.floor(dayEnd)}`;
        const options = {
            method: 'GET',
            headers: {
                'Authorization': localStorage.getItem('access-token-jwt'),
            },
        };

        return fetch(apiUrl, options)
            .then((res) => {
                if (res.status !== 200) throw res;
                return res.json();
            })
            .then((result) => {
                if (result.length > 0) {
                    const element = result.reduce((latest, item) =>
                        Moment(item.datetime).isAfter(Moment(latest.datetime)) ? item : latest,
                    result[0]);
                    const lastDataTime = Moment(element.datetime);
                    const currentTime = Moment();
                    const diffInMinutes = currentTime.diff(lastDataTime, 'minutes');

                    let iconStation = diffInMinutes >= 0 && diffInMinutes <= 20 ? 22 : 23;

                    const tempMark = {
                        lat: element.lat,
                        lng: element.lng,
                        img: img[iconStation].img,
                        id_source: sourceID,
                        datetime: element.datetime
                    };

                    const isSelectedStation = String(sourceID) === String(this.state.selectedStation);
                    if (!isSelectedStation) {
                        this.drawMarker(tempMark);
                        return tempMark;
                    }

                    if (element.addressStation === 255) {
                        const rainData = result.map((entry) => entry.rain);
                        const labels = result.map((entry) => Moment(entry.datetime).format('HH:mm'));
                        this.setState({ rainData }, () => this.generateRainChart(rainData, labels));
                    } else {
                        this.setState({ rainData: [] });
                    }
                    this.setState({
                        temperature: this.formatTemperature(element.air_temperature),
                        air_temperature: this.isValidWeatherValue(element.air_temperature) ? `${Number(element.air_temperature).toFixed(1)}` : '-',
                        humidity: this.isValidWeatherValue(element.air_humidity) ? `${Number(element.air_humidity).toFixed(1)} %` : '-',
                        air_humidity: this.isValidWeatherValue(element.air_humidity) ? `${Number(element.air_humidity).toFixed(1)}` : '-',
                        windSpeedRaw: this.isValidWeatherValue(element.wind_speed) ? Number(element.wind_speed) : null,
                        windSpeed: this.formatWindSpeed(element.wind_speed, this.state.windSpeedUnit),
                        deltaT: element.delta_t ? parseFloat(element.delta_t).toFixed(1) : '-',
                        windDirection: element.wind_direction ? `${element.wind_direction} °` : null,
                        thermalInversion: !element.temperature
                            ? this.props.res.DESABILITADO
                            : Number(element.air_temperature) - (Number(element.temperature) + 5) > 0
                                ? this.props.res.GRANDES_INVERSÃO_TERMICA
                                : Number(element.air_temperature) - (Number(element.temperature) + 1) > 0
                                    ? this.props.res.PODE_ESTAR_INVERSÃO_TERMICA
                                    : this.props.res.NAO_INVERSÃO_TERMICA,
                        pressure: element.pressure ? `${element.pressure} hPa` : '-',
                        datetime: this.formatDisplayDateTime(element.datetime),
                        loading: false,
                        flightConditions: element.delta_t >= 2 && element.delta_t <= 8
                            ? this.props.res.CONDICOES_ADEQUADAS
                            : element.delta_t > 10
                                ? this.props.res.CONDICOES_INADEQUADAS
                                : this.props.res.CONDICOES_ARRISCADAS
                    });

                    this.drawMarker(tempMark);
                    this.updateCoverageVisuals({ ...tempMark, delta_t: element.delta_t, wind_speed: element.wind_speed, wind_direction: element.wind_direction });
                    return tempMark;
                }
            })
            .catch((error) => {
                console.error(error);
                this.setState({ loading: false });
            });
    };

    generateRainChart = (rainData, labels) => {
        const chartElement = document.getElementById('chartRain');
        if (this.rainChart) {
            this.rainChart.destroy();
        }

        const fixRainData = this.props.res.LINGUA === 'en-US' ? rainData.map(value => Tools.mm_to_in(value)) : rainData;
        const totalRain = fixRainData.reduce((sum, value) => sum + value, 0).toFixed(1);
        const rainColors = fixRainData.map(value => value === 0 ? '#FAB288' : '#FA9F34');
        const maxRainValue = Math.max(...fixRainData);
        const yAxisMax = maxRainValue > 0 ? maxRainValue + 0.1 : 1;
        const ctx = chartElement.getContext('2d');

        this.rainChart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: labels,
                datasets: [{
                    label: `${this.props.res.CHUVA} (${this.props.res.LINGUA === 'en-US' ? this.props.res.IN : this.props.res.MM})`,
                    data: fixRainData,
                    backgroundColor: rainColors,
                    borderColor: '#FA9F34',
                    borderWidth: 1,
                    pointBackgroundColor: rainColors,
                    pointBorderColor: rainColors,
                    fill: false,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        min: 0,
                        max: yAxisMax,
                        ticks: {
                            color: 'white',
                            stepSize: 0.05,
                        },
                        title: {
                            display: true,
                            text: `${this.props.res.CHUVA} (${this.props.res.LINGUA === 'en-US' ? this.props.res.IN : this.props.res.MM})`,
                            color: 'white',
                        },
                        grid: {
                            color: '#6c757d'
                        }
                    },
                    x: {
                        ticks: {
                            color: 'white',
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: Math.floor(chartElement.clientWidth / 50),
                        },
                        title: {
                            display: true,
                            text: this.props.res.HORA,
                            color: 'white',
                        },
                        grid: {
                            color: '#6c757d',
                        }
                    },
                },
                plugins: {
                    legend: {
                        labels: {
                            color: 'white',
                        },
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                    },
                    title: {
                        display: true,
                        text: `${this.props.res.TOTAL_CHUVA_DIARIO}: ${totalRain} ${this.props.res.LINGUA === 'en-US' ? this.props.res.IN : this.props.res.MM}`,
                        color: 'white',
                        position: 'bottom',
                    },
                },
                elements: {
                    line: {
                        tension: 0.1,
                    },
                    point: {
                        radius: 3,
                    },
                },
            },
        });
    };

    handleStationChange = (event) => {
        this.setState({
            selectedStation: event.target.value, loading: true
        }, () => this.getDataById(this.state.selectedStation));
    };

    toggleWindSpeedUnit = () => {
        this.setState(prevState => {
            const newUnit = this.getNextWindSpeedUnit(prevState.windSpeedUnit);
            localStorage.setItem(this.getWindSpeedStorageKey(), newUnit);
            localStorage.setItem('windSpeedUnit', newUnit);

            return {
                windSpeedUnit: newUnit,
                windSpeed: this.formatWindSpeed(prevState.windSpeedRaw, newUnit)
            };
        });
    };

    render() {
        const { classes, res } = this.props;

        const climateData = [
            { label: res.TEMPERATURA_, value: this.state.temperature },
            { label: res.UMIDADE_, value: this.state.humidity },
            { label: res.VEL_VENTO, value: this.state.windSpeed },
            { label: res.DELTA_T, value: this.state.deltaT },
            { label: res.DIR_VENTO, value: this.state.windDirection },
            { label: res.PRESSAO, value: this.state.pressure },
            { label: res.INVERSAO_TERMICA, value: this.state.thermalInversion },
        ];

        return (
            <div className='dashboard-card' style={{ minWidth: '10rem', minHeight: '10rem' }}>
                <div className='divContent'
                    style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '.8rem',
                        backgroundColor: '#343a40',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <div className='cardheader' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FilterDramaIcon style={{ width: '1.7rem', height: '1.7rem', color: '#fa7d34' }} />
                            <span className='title' style={{ marginLeft: '0.5rem', color: 'white' }}>
                                {res.CLIMA_}
                            </span>
                        </div>
                        <FormControl style={{ width: 'fit-content' }}>
                            <InputLabel id="refresh-interval-label" style={{ fontSize: '0.875rem', color: 'white' }}>
                                {res.ATUALIZACAO}
                            </InputLabel>
                            <Select
                                labelId="refresh-interval-label"
                                value={this.state.refreshInterval / 60000}
                                onChange={this.handleIntervalChange}
                                className={classes.select}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            zIndex: 2000,
                                        },
                                    },
                                }}
                            >
                                {[1, 3, 5, 10, 15].map((interval) => (
                                    <MenuItem key={interval} value={interval}>
                                        {`${interval} ${res.MIN}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            onClick={this.toggleWindSpeedUnit}
                            variant="contained"
                            title={Tools.replaceString(res._DIRECAO_VENTO_EM_, this.state.windSpeedUnit)}
                            style={{ backgroundColor: 'rgb(250, 125, 52)', color: 'white' }}
                        >
                            {this.getWindSpeedUnitLabel(this.state.windSpeedUnit) + ' '} <SpeedIcon style={{ marginLeft: '0.25rem' }} />
                        </Button>
                        <FormControl style={{ width: 'fit-content' }}>
                            <InputLabel id="station-select-label" style={{ fontSize: '0.875rem', color: 'white' }}>
                                {res.ESTACAO}
                            </InputLabel>
                            <Select
                                labelId="station-select-label"
                                value={this.state.selectedStation}
                                onChange={this.handleStationChange}
                                className={classes.select}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            zIndex: 2000,
                                        },
                                    },
                                    PopperProps: {
                                        disablePortal: false,
                                        modifiers: {
                                            preventOverflow: {
                                                enabled: true,
                                                boundariesElement: 'viewport',
                                            },
                                        },
                                    },
                                }}
                            >
                                {this.state.stations.map((station) => (
                                    <MenuItem key={station.id} value={station.id}>
                                        {station.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                    <div className='cardmiddle'
                        style={{
                            overflowY: 'auto',
                            flexGrow: 1,
                            maxHeight: '100%',
                            padding: '0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'start'
                        }}
                    >
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', width: '100%' }}>
                            {climateData.map((data, index) => {
                                if (data.label === res.INVERSAO_TERMICA) {
                                    const thermalInversionColor = data.value === res.DESABILITADO ? 'white' : data.value === res.GRANDES_INVERSÃO_TERMICA
                                        ? 'red'
                                        : data.value === res.PODE_ESTAR_INVERSÃO_TERMICA
                                            ? 'yellow'
                                            : 'lawngreen';

                                    return (
                                        <Tooltip key={index} title={
                                            <div
                                                style={{
                                                    backgroundColor: '#fff',
                                                    padding: '10px',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    width: '25.25rem'
                                                }}
                                            >
                                                {data.value === res.NAO_OCORRENDO_INVERSAO_TERMICA
                                                    ?
                                                    <img style={{ marginTop: '2.5rem', maxHeight: '12rem', borderRadius: '1rem' }} src={normalFlowExample} alt={res.FLUXO_NORMAL} />
                                                    :
                                                    <img style={{ marginTop: '2.5rem', maxHeight: '12rem', borderRadius: '1rem' }} src={thermalInversionExample} alt={res.FLUXO_INVERSAO_TERMICA} />
                                                }
                                            </div>
                                        }
                                            placement="top"
                                            arrow
                                            classes={{ tooltip: classes.customWidthTooltip }}
                                        >
                                            <div style={{ gridColumn: 'span 3', borderRadius: '0.5rem', backgroundColor: '#252a2f', textAlign: 'center' }}>
                                                <span style={{ display: 'block', color: '#fa7d34' }}>
                                                    {data.label}
                                                </span>
                                                <span style={{ display: 'flex', justifyContent: 'center', color: thermalInversionColor, fontWeight: 'bold', fontSize: '1.25rem' }}>
                                                    {this.state.loading ? <Skeleton width="12rem" /> : data.value}
                                                </span>
                                            </div>
                                        </Tooltip>
                                    );
                                }

                                if (data.label === res.DELTA_T) {
                                    let deltaTColor = 'white';
                                    if (this.state.deltaT >= 2 && this.state.deltaT <= 8) {
                                        deltaTColor = 'lawngreen';
                                    } else if (this.state.deltaT > 10) {
                                        deltaTColor = 'red';
                                    } else if (this.state.deltaT === '-') {
                                        deltaTColor = 'white';
                                    } else {
                                        deltaTColor = 'yellow';
                                    }

                                    return (
                                        <Tooltip key={index} title={
                                            <div
                                                style={{
                                                    backgroundColor: '#fff',
                                                    padding: '10px',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    width: '22.5rem'
                                                }}
                                            >
                                                <DeltaTHeat
                                                    atual={this.state.deltaT}
                                                    humidade={this.state.air_humidity}
                                                    temperatura={this.state.air_temperature}
                                                    res={res}
                                                />
                                            </div>
                                        }
                                            placement="top"
                                            arrow
                                            classes={{ tooltip: classes.customWidthTooltip }}
                                        >
                                            <div style={{ borderRadius: '0.5rem', backgroundColor: '#252a2f', textAlign: 'center' }}>
                                                <span style={{ display: 'block', color: '#fa7d34' }}>
                                                    {data.label}
                                                </span>
                                                <span style={{ display: 'flex', justifyContent: 'center', color: deltaTColor, fontWeight: 'bold', fontSize: '1.25rem' }}>
                                                    {this.state.loading ? <Skeleton width="4rem" /> : data.value}
                                                </span>
                                            </div>
                                        </Tooltip>
                                    );
                                }

                                if (data.label === res.DIR_VENTO) {
                                    return (
                                        <div key={index} className={classes.windDirectionContainer} style={{ borderRadius: '0.5rem', backgroundColor: '#252a2f', textAlign: 'center' }}>
                                            <span style={{ display: 'block', color: '#fa7d34' }}>{data.label}</span>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {this.state.windDirection ? (
                                                    <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.25rem', margin: '0.5rem', whiteSpace: 'nowrap' }}>
                                                        {data.value}
                                                    </span>
                                                ) : (
                                                    <Skeleton width="2rem" />
                                                )}
                                                {this.state.windDirection ? (
                                                    <div className={classes.circle}>
                                                        <div className={`${classes.windDirection} ${classes.north}`}>{res.N}</div>
                                                        <div className={`${classes.windDirection} ${classes.south}`}>{res.S}</div>
                                                        <div className={`${classes.windDirection} ${classes.east}`}>{res.L}</div>
                                                        <div className={`${classes.windDirection} ${classes.west}`}>{res.O}</div>
                                                        <img
                                                            src={windArrowImage}
                                                            alt={res.DIRECAO_VENTO}
                                                            className={classes.windDirectionArrow}
                                                            style={{ transform: `translate(-50%, -50%) rotate(${parseFloat(this.state.windDirection) + 180}deg)` }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <Skeleton variant="rect" width="1rem" height="1rem" className={classes.windDirectionArrow} />
                                                )}
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={index} style={{ borderRadius: '0.5rem', backgroundColor: '#252a2f', textAlign: 'center' }}>
                                        <span style={{ display: 'block', color: '#fa7d34' }}>
                                            {data.label}
                                        </span>
                                        <span style={{ display: 'flex', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '1.25rem' }}>
                                            {this.state.loading ? <Skeleton width="4rem" /> : data.value}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        {this.state.rainData.length > 0 && (
                            <div style={{ marginTop: '1rem' }}>
                                <canvas id="chartRain" height="150"></canvas>
                            </div>
                        )}
                        <div className="divContent divRelative map-p" style={{ backgroundColor: 'rgb(52, 58, 64)', height: '16rem', minHeight: '8rem', width: '100%', padding: '0', borderRadius: '0.5rem' }}>
                            <div className="containerTop" style={{ padding: '0' }}></div>
                            <div className={'wrapperMap'}>
                                <div style={{ width: '100%', height: '100%', position: 'absolute', zIndex: '1111', display: 'none' }}></div>

                                <div id="mapid" className={classes.mapCommonStyle}></div>
                                <div className="zoom">
                                    <Button className="buttonS buttonMenu" onClick={() => this.zoomInL()}>
                                        <AddIcon className="iconMap" />
                                    </Button>
                                    <Button className="buttonS buttonMenu" onClick={() => this.zoomOutL()}>
                                        <RemoveIcon className="iconMap" />
                                    </Button>

                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='cardfooter'>
                        <span className='cardfooter-content' style={{ paddingTop: '.7rem' }}>
                            <span style={{ display: 'inline-table', paddingLeft: '1rem', width: '50%' }}>
                                <span style={{ display: 'table-row', width: '100%', color: '#fa7d34' }}>
                                    {res.ATUALIZADO_}
                                </span>
                            </span>
                            <span style={{ display: 'inline-table', paddingRight: '1rem', width: '50%', fontSize: '1.25rem', fontWeight: 'bold', color: 'white' }}>
                                <span style={{ display: 'table-row', width: '100%' }}>
                                    {this.state.loading ? <Skeleton width="10rem" /> : this.state.datetime}
                                </span>
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        );
    }
}

export default withStyles(styles)(DiaryClimate);
